// Store rows under the admin sidebar's "Awaiting Shipment" (ShipStation
// parity, Volkan 2026-09-25): every connected shop of every org in one flat
// list, each row naming its org, so labels can be printed store by store.
//
// Counts come from the whole-platform aggregate
// (GET /admin/orders/awaiting-shipment-stores), never from a loaded list page,
// so the rows always add up to the Awaiting Shipment badge above them.

import type { SidebarSubItem } from '../../components/Sidebar'

export type AdminAwaitingShipmentStore = {
  store_integration_id: string | null
  store_origin: string
  shop_name: string | null
  org_name: string | null
  count: number
}

const ORIGIN_PREFIX = 'origin:'

function providerLabel(provider: string): string {
  if (provider === 'ebay') return 'eBay'
  if (provider === 'woocommerce') return 'WooCommerce'
  return provider.charAt(0).toUpperCase() + provider.slice(1)
}

/** The key the list filter and the sidebar link agree on: the shop id, or
 * ``origin:<provider>`` for orders that carry no shop. */
export function adminStoreFilterKey(row: AdminAwaitingShipmentStore): string {
  return row.store_integration_id ?? `${ORIGIN_PREFIX}${row.store_origin}`
}

export function adminStoreLink(key: string): string {
  return `/admin/orders?store=${encodeURIComponent(key)}`
}

function adminStoreLabel(row: AdminAwaitingShipmentStore): string {
  return row.shop_name || `Other ${providerLabel(row.store_origin)} orders`
}

/** Sidebar leaves: EVERY connected shop (Volkan 2026-09-25 — "mağazaların
 * tamamı"), busiest first, then the quiet ones by name. A shopless-order row
 * only appears when such orders exist, so the rows add up to the badge.
 *
 * The org name rides along only when the list actually spans more than one org
 * — with a single tenant it is the same word on every row and only steals the
 * width the shop name needs. */
export function buildAdminStoreItems(rows: AdminAwaitingShipmentStore[]): SidebarSubItem[] {
  const orgs = new Set(rows.map((row) => row.org_name).filter(Boolean))
  const showOrg = orgs.size > 1
  return rows
    .filter((row) => row.store_integration_id !== null || row.count > 0)
    .slice()
    .sort(
      (a, b) =>
        b.count - a.count ||
        adminStoreLabel(a).localeCompare(adminStoreLabel(b), undefined, { numeric: true }),
    )
    .map((row) => ({
      to: adminStoreLink(adminStoreFilterKey(row)),
      label: adminStoreLabel(row),
      hint: showOrg ? (row.org_name ?? undefined) : undefined,
      badge: row.count,
    }))
}

/** Label for the active store chip on the list page. */
export function adminStoreChipLabel(
  key: string,
  rows: AdminAwaitingShipmentStore[],
): string {
  const match = rows.find((row) => adminStoreFilterKey(row) === key)
  if (match) return adminStoreLabel(match)
  if (key.startsWith(ORIGIN_PREFIX)) {
    return `Other ${providerLabel(key.slice(ORIGIN_PREFIX.length))} orders`
  }
  return 'Selected store'
}


export type AdminStoreOptionGroup = {
  provider: string
  label: string
  options: { key: string; label: string }[]
}

/** The list page's Store dropdown: every connected shop, grouped by
 * marketplace (Volkan 2026-10-23 — "store filtresi mağaza listesi"). With an
 * org selected only that org's shops are offered. A shopless-order row only
 * appears when such orders exist, same as the sidebar. Org names ride along
 * when the shops span more than one org. */
export function buildAdminStoreOptionGroups(
  rows: AdminAwaitingShipmentStore[],
  orgName?: string | null,
): AdminStoreOptionGroup[] {
  const visible = rows
    .filter((row) => row.store_integration_id !== null || row.count > 0)
    .filter((row) => !orgName || row.org_name === orgName)
  const orgs = new Set(visible.map((row) => row.org_name).filter(Boolean))
  const showOrg = orgs.size > 1
  const groups = new Map<string, AdminStoreOptionGroup>()
  for (const row of visible) {
    const group = groups.get(row.store_origin) ?? {
      provider: row.store_origin,
      label: providerLabel(row.store_origin),
      options: [],
    }
    const name = adminStoreLabel(row)
    group.options.push({
      key: adminStoreFilterKey(row),
      label: showOrg && row.org_name ? `${name} · ${row.org_name}` : name,
    })
    groups.set(row.store_origin, group)
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      options: group.options.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** The Store dropdown fed by the list's own ``store_facets`` (Volkan
 * 2026-10-23 — "sonuç listesinde o mağaza yoksa çıkmasına gerek var mı?"):
 * only shops that have an order under the current tab and filters, each with
 * its count. The server leaves the store filter out of that count, so with one
 * shop picked the others are still offered. The rows are already narrowed to
 * the selected org server-side, so no org-name filter runs here: a shopless
 * row carries no org name and would otherwise be dropped. */
export function buildAdminStoreFacetGroups(
  rows: AdminAwaitingShipmentStore[],
): AdminStoreOptionGroup[] {
  const counts = new Map(rows.map((row) => [adminStoreFilterKey(row), row.count]))
  return buildAdminStoreOptionGroups(rows.filter((row) => row.count > 0)).map((group) => ({
    ...group,
    options: group.options.map((option) => ({
      ...option,
      label: `${option.label} (${counts.get(option.key) ?? 0})`,
    })),
  }))
}
